"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { IconEllipse } from "@/components/icons/IconEllipse";

const VALUES_ROW_1 = [
  { text: "Recovery Groups", italic: true },
  { text: "Recovery Support", italic: false },
  { text: "Inclusive", italic: true },
  { text: "Proactive", italic: false },
  { text: "Multi-dimensional", italic: true },
  { text: "Redemptive", italic: false },
  { text: "Sustainable", italic: true },
  { text: "Comprehensive", italic: false },
] as const;

const VALUES_ROW_2 = [
  { text: "Evidence-based", italic: false },
  { text: "Dynamic", italic: true },
  { text: "Vision-orientated", italic: false },
  { text: "Strategic", italic: true },
  { text: "Integrative", italic: false },
  { text: "Community-driven", italic: true },
  { text: "Pioneering", italic: false },
  { text: "Accessible", italic: true },
] as const;

function ValuesList({
  values,
}: {
  values: ReadonlyArray<{ text: string; italic: boolean }>;
}) {
  return (
    <>
      {values.map((value, i) => (
        <span className="flex shrink-0 items-center" key={value.text}>
          <span
            className={
              value.italic
                ? "px-4 font-semibold font-serif text-base italic leading-6 sm:text-lg md:text-2xl lg:text-3xl"
                : "px-4 font-medium text-base leading-6 sm:text-lg md:text-2xl lg:text-3xl"
            }
          >
            {value.text}
          </span>
          {i < values.length - 1 && (
            <IconEllipse
              className="shrink-0"
              fill="black"
              height={10}
              width={10}
            />
          )}
        </span>
      ))}
    </>
  );
}

export function ValuesMarqueeBanner() {
  const containerRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  // Row 1 drifts right → left; Row 2 drifts left → right (mirrors BannerSection)
  const row1X = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    ["50%", "0%", "-50%"]
  );
  const row2X = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    ["-50%", "0%", "50%"]
  );

  return (
    <section
      className="relative w-full overflow-hidden py-8 sm:py-12 md:py-16"
      ref={containerRef}
    >
      <div className="flex flex-col gap-4 sm:gap-6">
        <div className="flex w-full justify-center py-2">
          <motion.div
            className="flex w-max items-center whitespace-nowrap"
            style={{ x: row1X, willChange: "transform" }}
          >
            <ValuesList values={VALUES_ROW_1} />
          </motion.div>
        </div>

        <div className="flex w-full justify-center py-2">
          <motion.div
            className="flex w-max items-center whitespace-nowrap"
            style={{ x: row2X, willChange: "transform" }}
          >
            <ValuesList values={VALUES_ROW_2} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
